import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import styles from './Achievements.module.scss';
import { Web3Context } from '../../web3';
import { createContract } from '../../services/contractService';
import { ABIContext } from '../../components/Context';
import Loader from '../../assets/imgs/loader.gif';
import axios from 'axios';
import { Button } from 'react-bootstrap';

const SOULBOUND_TOKENS_IMAGE_URL = process.env.REACT_APP_GOOGLE_STORAGE_BASE_URL + "/badges/";
const SOULBOUND_TOKENS_PREDEFINED_JSON_MAP = process.env.REACT_APP_GOOGLE_STORAGE_BADGES_CONFIG_URL;

const Achievements = (props) => {
    const [t] = useTranslation();
    const [loading, setLoading] = useState(false);
    const [userBadges, setUserBadges] = useState([]);
    // Tokens Map will store predefined information about all SBT types
    const [tokensMap, setTokensMap] = useState([]);
    const { selectedAccount, networkId, connect } = useContext(Web3Context);
    const [abiConfig] = useContext(ABIContext);
    const [badgeContract, setBadgeContract] = useState(null);
    const { 
        polygonDeploymentContext, 
        polygonProvider 
    } = abiConfig;

    const renderSingleBadge = (badgeName, badgeImg, badgeId) => {
        return (
            <div key={badgeId} className={styles.badge}>
                <div className={styles.badge_image}>
                    <img src={badgeImg} alt="badge" />
                </div>
                <div className={styles.badge_name}>
                    {badgeName}
                </div>
                <div className={styles.badge_achieved}>
                    {'Achieved'}
                </div>
            </div>
        )
    }

    const renderBadges = (userBadges) => {
        return(
            userBadges.map((badge) => {
                if(badge.balance > 0) {
                    return(
                        renderSingleBadge(badge.name, badge.img, badge.id)
                    )
                }
            })
        )
    }

    const renderWalletConnect = (
        <div className={styles.connect_wallet}>
            <div className={styles.connect_wallet_text}>{t('CONNECT_WALLET_ACCESS_BADGES')}</div>
                <div className={styles.description_text}>{t('NO_ITEMS_WITHOUT_CONNECT')}</div>
                <Button variant="cdh-general-blue-dbrown" className={styles.primary_btn} onClick={connect}>{t('CONNECT')}</Button>
        </div>
    )

    const renderBadgesContainer = (
        <section className={styles.achievements_main_container}>
            <div className={styles.total_achievements_plus_check_hide}>
                <div className={styles.total_achievements_count}>
                    {`Total: ${userBadges.length}`}
                </div>

                {/* Section for hide missed badges checkbox */}
                <div className={styles.achievements_check_hide}>
                    <input type="checkbox" value="hide-missed-badge" />
                    &nbsp;
                    <span className={styles.hide_message_text}>
                        {t('HIDE_MISSED_BADGES')}
                    </span>
                </div>
            </div>

            <div className={styles.achievements_badges}>
                {/* Wrapper function for list of badges and use map to return individual badges */}
                    { renderBadges(userBadges) }
            </div>

        </section>
    )

    const redirectGetStartedBtn = () => {
        window.open(
            "https://crazydefenseheroes.fandom.com/wiki/Category:Complete_Guideline",
            "_blank"
        );
    }

    const renderNoBadgesFound = (
        <div className={styles.achievements_no_badge}>
            <div className={styles.achievements_no_badge_text}>
                { t('NO_BADGES_FOUND') }
            </div>
            <div className={styles.achievements_participate_text}>
                { t('PARTICIPATE_IN_TOWER_EVENTS') }
            </div>
            <div className={styles.achievements_get_started_btn}>
                <Button variant="primary" className={styles.achievements_get_started_btn} onClick={ () => redirectGetStartedBtn() }>
                    { t('GET_STARTED') }
                </Button>
            </div>
        </div>
    )

    const renderBadgesLoading = (
        <div className={styles.achievements_loading}>
            <div>
                <img className={styles.achievements_loading_icon} src={Loader} alt="loading" />
            </div>
            <div className={styles.achievements_loading_text}>
                {t('LOADING_BADGES')}
            </div>
        </div>
    )

    const initializeBadgeContract = async () => {
        try {
            let contract;
            contract = await createContract(polygonDeploymentContext, 'CDHBadges', polygonProvider.connection.url);
            setBadgeContract(contract);
        } catch (err) {
            console.log(err);
        }
    }

    const fetchBadges = async () => {
        try {
            setLoading(true);
            let userBadges = [];
            for(const token of tokensMap) {
                const balanceForParticularTokenType = await badgeContract.methods.balanceOf(selectedAccount, token.tokenId).call();
                if(balanceForParticularTokenType >= 1) {
                    const badgeImageUrl = `${SOULBOUND_TOKENS_IMAGE_URL}${token.tokenId}.png`;
                    
                    const singleBadgeInfo = { 
                        "name": token.name, 
                        "id": token.tokenId,
                        "img": badgeImageUrl,
                        "balance": balanceForParticularTokenType
                    }
    
                    userBadges.push(singleBadgeInfo);
                }
            };
            setUserBadges(userBadges);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if(polygonDeploymentContext && polygonProvider) {
            initializeBadgeContract();
        }
    }, [networkId, polygonProvider, polygonDeploymentContext]);

    useEffect(() => {
        if (selectedAccount && badgeContract && tokensMap.length >= 0 && !loading) {
            fetchBadges();
        }

        if (!selectedAccount) {
            setUserBadges([]);
        }
    }, [badgeContract, selectedAccount, tokensMap])

    // fetch SBT token maps from google storage url upon loading page
    useEffect(() => {        
        try {
            setLoading(true);
            const fetchTokensMap = async () => {
                const map = await axios.get(SOULBOUND_TOKENS_PREDEFINED_JSON_MAP).catch((err) => err.response);
                if(map && map?.status === 200 && map?.data?.length > 0) {
                    setTokensMap(map.data);
                }
            }
    
            fetchTokensMap();
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [])
    
    return (
        <div className={styles.achievements_container}>
            <section className={styles.header_container}>
                <div className={styles.header_items} >
                    <div className={styles.header_title}>
                        <div className={styles.header}>{t('ACHIEVEMENTS')}</div>
                        <div className={styles.details}>
                            {t('TOWER_TOKEN_DESCRIPTION')}
                        </div>
                    </div>
                </div>
            </section>

            {/* Render main container if loading is false else show the loader */}
            { selectedAccount ?
                (loading ?
                    renderBadgesLoading
                : (userBadges.length > 0)
                    ? renderBadgesContainer
                    : renderNoBadgesFound
                ):
                    renderWalletConnect
                    
            }
            
        </div>
        
    )
}

export { Achievements };
